import { ErrorService } from '@/shared/service/errorService'
import { type ProgEvent } from '@/shared/types/progEvent.type'
import { type Program } from '@/shared/types/program.type'
import { create } from '@/shared/utils/zustandVue'

import { makeTranslatableText } from '../../../types/TranslatableText.type'
import { t } from '../../i18n/i18n'
import { SnackbarService } from '../../shared/snackbar/snackbar.service'
import { ProgEventService } from '../progEvent/progEvent.service'
import { ProgramsService } from './programs.service'

class ProgramsState {
  programs: Record<string, Program> = {}
  deletingProgramId: string | null = null
  editingProgramId: string | null = null
  loading = false
  showModal = false
  events: ProgEvent[] = []
}

type ProgramsStore = ProgramsState & {
  load: () => void
  unload: () => void
  setDeleting: (id: string) => void
  setNotDeleting: () => void
  setAdding: () => void
  setEditing: (id: string) => void
  reset: () => void
  list: () => Promise<void>
  setDeletingProgram: (id: string) => Promise<void>
  deleteProgram: () => Promise<void>
  addProgram: (program: Program) => Promise<void>
}
/**
 * Compute the `getPrograms` getter value reactively from the store state.
 * Preserves the original Vuex getter logic: map + sort by fr name.
 */
export function computeGetPrograms(state: ProgramsState): Program[] {
  return Object.values(state.programs)
    .map((p) => ({ ...p }) as Program)
    .sort((a, b) =>
      ((a.names as { fr?: string } | null)?.fr ?? '').localeCompare(
        (b.names as { fr?: string } | null)?.fr ?? '',
      ),
    )
}

/**
 * Compute the `getProgram` getter value reactively from the store state.
 * Preserves the original Vuex getter logic: return the edited program or a
 * blank default.
 */
export function computeGetProgram(state: ProgramsState): Program {
  return {
    ...(state.editingProgramId != null
      ? state.programs[state.editingProgramId]
      : {
          names: makeTranslatableText(''),
          color: '#FFFFFFFF',
          isDeletable: true,
        }),
  } as Program
}

export const useProgramsStore = create<ProgramsStore>((set, get) => ({
  ...new ProgramsState(),
  load: () => {
    set({ loading: true })
  },
  unload: () => {
    set({ loading: false })
  },
  setDeleting: (id) => {
    set({ deletingProgramId: id })
  },
  setNotDeleting: () => {
    set({ deletingProgramId: null })
  },
  setAdding: () => {
    set({ showModal: true })
  },
  setEditing: (id) => {
    set({ editingProgramId: id, showModal: true })
  },
  reset: () => {
    set({ showModal: false, editingProgramId: null })
  },
  list: async () => {
    get().load()
    try {
      const programs = await ProgramsService.getAll()
      set({
        programs: programs.reduce((acc: Record<string, Program>, program) => {
          acc[program._id] = program
          return acc
        }, {}),
      })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  setDeletingProgram: async (id) => {
    get().load()
    try {
      set({ deletingProgramId: id })
      const events = await ProgEventService.getProgramEventByProgram(id)
      set({ events })
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
  deleteProgram: async () => {
    get().load()
    try {
      const { deletingProgramId, programs } = get()
      if (deletingProgramId == null) return
      await ProgramsService.delete(deletingProgramId)
      SnackbarService.info(
        t('PROGRAMS.SNACK_BAR.DELETE', {
          string: programs[deletingProgramId].names.fr,
        }),
      )
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().setNotDeleting()
      get().unload()
    }
  },
  addProgram: async (program) => {
    get().load()
    try {
      let message = ''
      if (get().editingProgramId != null) {
        await ProgramsService.updateProgram(program)
        message = t('PROGRAMS.SNACK_BAR.EDIT', {
          string: program.names.fr,
        })
      } else {
        await ProgramsService.addProgram(program)
        message = t('PROGRAMS.SNACK_BAR.ADD', {
          string: program.names.fr,
        })
      }
      SnackbarService.info(message)
      get().reset()
      await get().list()
    } catch (e) {
      ErrorService.handleError(e)
    } finally {
      get().unload()
    }
  },
}))
